import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, functions } from '../firebase';
import { normalizeDecade, buildPedalPricingKey } from '../utils/reverbPricing';
import { getDevPricingOverrides, resolveDevOverride } from '../utils/devPricingOverrides';
import { storedDocMatchesIdentity,
  verifiedFreshDoc,
  pricingIdentityKey,
  getPricingEntry,
  setPricingEntry,
  dedupePricingFetch,
} from '../utils/pricingDocCache';

// The doc store and the in-flight map both live in pricingDocCache.js now
// (they used to be this module's private Map). PriceIndicator fetches the
// same callable for the same identity key, so sharing them is what makes a
// listing page's two PriceIndicator instances cost ONE call instead of two,
// and what keeps a return-to-view refresh from fanning out per component.
function cacheKey(make, model, decade) {
  return pricingIdentityKey(make, model, decade);
}

/**
 * ALWAYS goes to the network (dev override first), deduplicated by pricing
 * identity, and stamps the shared store on resolve. Exported because
 * PriceIndicator needs this exact behaviour: it must not short-circuit on a
 * fresh cache entry at mount (see the comment at its call site — the
 * cold-start recovery for the Add Pedal price check depends on a remount
 * actually re-asking), but it must share the dedupe and the stamp.
 */
export function loadPricingDoc(make, model, normDecade) {
  const key = cacheKey(make, model, normDecade);
  return dedupePricingFetch(key, async () => {
    // Dev-only v2 preview overlay (no-op in production builds): lets local
    // verification see corrected pricing before the v2 functions deploy.
    const overrides = await getDevPricingOverrides();
    const docKey = buildPedalPricingKey(make, model, normDecade);
    const resolved = resolveDevOverride(overrides, docKey);
    if (resolved !== undefined) {
      setPricingEntry(key, resolved);
      return resolved;
    }
    try {
      const fetchFn = httpsCallable(functions, 'fetchReverbPricingOnDemand');
      const result = await fetchFn({ make, model, decade: normDecade });
      const data = result?.data || null;
      setPricingEntry(key, data);
      return data;
    } catch {
      setPricingEntry(key, null);
      return null;
    }
  });
}

async function fetchPricingDoc(make, model, normDecade) {
  const key = cacheKey(make, model, normDecade);
  const fresh = verifiedFreshDoc(getPricingEntry(key), make, model);
  if (fresh !== undefined) return fresh;
  return loadPricingDoc(make, model, normDecade);
}

// Fire-and-forget prewarm for the pricing callable. Unlike the suggestion
// search (isolated in the tiny functions-suggestions/ codebase),
// fetchReverbPricingOnDemand lives in the heavy main functions/ codebase
// (Vision, sharp, Stripe, Shippo, …), so its cold start is ~6-7s and the very
// first real lookup often resolves to null before the instance is ready —
// which is why the price check "doesn't load until you go back and retry".
//
// Calling it with empty make/model short-circuits server-side: the callable
// throws invalid-argument AFTER auth but BEFORE any Reverb call or Firestore
// write, so this pays only the container spin-up — zero Reverb quota, zero
// writes. We fire it the moment the user lands on Add Pedal so the instance is
// hot well before they pick a pedal and the real make/model lookup fires.
// Deduped per page load; a failure clears the flag so a later mount can retry.
let pricingPrewarmInFlight = false;
// Re-exported from the shared pricing-doc access layer, where it lives so
// both this hook and usePedalMarketValues can use it WITHOUT importing each
// other. (A hook-to-hook import left it undefined at call time, and the
// surrounding try/catch swallowed the TypeError into a cached miss — the
// grid silently showed no price instead of verifying.)
export { storedDocMatchesIdentity };

export function prewarmReverbPricing() {
  if (pricingPrewarmInFlight) return;
  if (!auth.currentUser) return; // callable requires auth; skip when logged out
  pricingPrewarmInFlight = true;
  try {
    const fetchFn = httpsCallable(functions, 'fetchReverbPricingOnDemand');
    // Empty args → server throws invalid-argument before touching Reverb; the
    // rejection is expected and swallowed. The point is purely to warm the box.
    fetchFn({ make: '', model: '' }).catch(() => { pricingPrewarmInFlight = false; });
  } catch {
    pricingPrewarmInFlight = false;
  }
}

/**
 * Fetches Reverb pricing once per (make, model, decade) with module-level dedupe.
 *
 * Logged-in:  calls the fetchReverbPricingOnDemand Cloud Function (existing path).
 * Logged-out: reads pedalPricing/{key} directly from Firestore (public read).
 *             If no cached doc exists, returns null — the Cloud Function and
 *             Reverb API are never contacted for unauthenticated users.
 *
 * NO RETURN-TO-VIEW REFRESH HERE, deliberately (owner instruction,
 * 2026-10-12: "do not add polling to every Browse card"). The only consumer
 * of this hook is PriceDealThumbnailBadge, i.e. Browse thumbnails — dozens
 * of distinct pricing identities on one screen, which is exactly the fan-out
 * the owner excluded. A lookup still runs on mount / identity change and
 * still consults the TTL, unchanged. What this hook DOES now take part in is
 * the shared store and the shared in-flight map, so a refresh triggered by a
 * pricing screen (PriceIndicator) also freshens what the next badge lookup
 * will read, without any badge ever firing a request of its own.
 */
export function useReverbPricing(make, model, decade) {
  const trimmedMake = (make || '').trim();
  const trimmedModel = (model || '').trim();
  const normDecade = normalizeDecade(decade);
  const key = cacheKey(trimmedMake, trimmedModel, normDecade);

  const [pricingDoc, setPricingDoc] = useState(
    () => verifiedFreshDoc(getPricingEntry(key), trimmedMake, trimmedModel) ?? null);
  const [loading, setLoading] = useState(
    () => verifiedFreshDoc(getPricingEntry(key), trimmedMake, trimmedModel) === undefined
      && !!trimmedMake && !!trimmedModel
  );

  useEffect(() => {
    if (!trimmedMake || !trimmedModel) {
      setPricingDoc(null);
      setLoading(false);
      return undefined;
    }

    // Logged-out path: direct Firestore read, no Cloud Function, no Reverb API.
    if (!auth.currentUser) {
      const fsKey = buildPedalPricingKey(trimmedMake, trimmedModel, normDecade);
      if (!fsKey) {
        setLoading(false);
        return undefined;
      }

      // Reuse module-level cache in case a prior logged-in fetch already
      // populated it — only while FRESH (TTL): transaction evidence changes
      // docs at event time, so stale entries refetch.
      const cachedData = verifiedFreshDoc(getPricingEntry(key), trimmedMake, trimmedModel);
      if (cachedData !== undefined) {
        setPricingDoc(cachedData);
        setLoading(false);
        return undefined;
      }

      setLoading(true);
      let cancelled = false;
      // Dev-only v2 preview overlay: the LOGGED-OUT path must honor it too —
      // missing it here meant logged-out browse showed old docs (no badges)
      // while logged-in flows showed override data. No-op in production.
      getDevPricingOverrides().then((overrides) => {
        if (cancelled) return;
        const resolved = resolveDevOverride(overrides, fsKey);
        if (resolved !== undefined) {
          setPricingEntry(key, resolved);
          setPricingDoc(resolved);
          setLoading(false);
          return;
        }
        getDoc(doc(db, 'pedalPricing', fsKey)).then((snap) => {
          if (!cancelled) {
            const raw = snap.exists() ? snap.data() : null;
            // IDENTITY VERIFICATION — FAIL CLOSED. Before '+' was preserved
            // in the document key, DigiTech Trio and Trio+ both resolved to
            // digitech_trio_modern, so a document found under a legacy key
            // may belong to the OTHER product. The logged-in path goes
            // through the callable, which verifies server-side; this
            // logged-out path reads Firestore directly, so it has to check
            // here. A document that does not prove it is this product, or
            // that records no identity at all, is discarded — the indicator
            // then shows nothing rather than another pedal's prices.
            const data = raw && storedDocMatchesIdentity(raw, trimmedMake, trimmedModel)
              ? raw : null;
            // Only cache hits so a later logged-in session can still trigger the CF.
            if (data) setPricingEntry(key, data);
            setPricingDoc(data);
            setLoading(false);
          }
        }).catch(() => {
          if (!cancelled) {
            setPricingDoc(null);
            setLoading(false);
          }
        });
      });
      return () => { cancelled = true; };
    }

    // Logged-in path: existing Cloud Function behaviour, with the TTL check.
    let cancelled = false;
    const cachedData = verifiedFreshDoc(getPricingEntry(key), trimmedMake, trimmedModel);
    if (cachedData !== undefined) {
      setPricingDoc(cachedData);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    fetchPricingDoc(trimmedMake, trimmedModel, normDecade).then((data) => {
      if (!cancelled) {
        setPricingDoc(data);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [trimmedMake, trimmedModel, normDecade, key]);

  return { pricingDoc, loading, normDecade };
}
